import { Injectable } from '@angular/core';

import dayjs, { Dayjs, ManipulateType, UnitType } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';

import { IDateProvider } from '@app/interfaces/date-provider';

dayjs.extend(utc);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export enum Inclusivity {
  excludeBoundaries = '()',
  includeBoundaries = '[]',
  includeStartOnly = '[)',
  excludeStartOnly = '(]'
}

@Injectable({
  providedIn: 'root'
})
export class DayjsService implements IDateProvider {
  convertToUTC(date: Date): string {
    return dayjs(date).utc().local().format();
  }

  convertToDate(date: string | number | Date | Dayjs): Date {
    return dayjs(date).toDate();
  }

  convertToISOString(date: string | Date): string {
    return dayjs(date).toISOString();
  }

  compareIsBefore(
    start_date: string | Date,
    end_date: string | Date,
    unit?: UnitType
  ): boolean {
    return dayjs(end_date).isBefore(start_date, unit);
  }

  compareIsAfter(
    start_date: string | Date,
    end_date: string | Date,
    unit?: dayjs.OpUnitType
  ): boolean {
    return dayjs(end_date).isAfter(start_date, unit);
  }

  compareIsBetween(
    date: string | Date,
    start_date: string | Date,
    end_date: string | Date,
    unit?: UnitType,
    inclusivity?: Inclusivity
  ): boolean {
    return dayjs(date).isBetween(
      dayjs(start_date),
      dayjs(end_date),
      unit,
      inclusivity
    );
  }

  compareIsSame(start_date: string | Date, end_date: string | Date): boolean {
    return dayjs(start_date).isSame(end_date);
  }

  compareIsSameOrBefore(
    start_date: string | Date,
    end_date: string | Date,
    unit?: UnitType
  ): boolean {
    return dayjs(start_date).isSameOrBefore(end_date, unit);
  }

  compareIsSameOrAfter(
    start_date: string | Date,
    end_date: string | Date,
    unit?: UnitType
  ): boolean {
    return dayjs(start_date).isSameOrAfter(end_date, unit);
  }

  diff(start_date: Date, end_date: Date, unit: UnitType): number {
    return dayjs(start_date).diff(end_date, unit);
  }

  dateNow(): Date {
    return dayjs().toDate();
  }

  addMonths(months: number, custom_date?: Date): Date {
    const date = custom_date ? dayjs(custom_date) : dayjs();
    return date.add(months, 'months').toDate();
  }

  addDays(days: number, custom_date?: Date): Date {
    const date = custom_date ? dayjs(custom_date) : dayjs();
    return date.add(days, 'days').toDate();
  }

  addHours(hours: number, custom_date?: Date): Date {
    const date = custom_date ? dayjs(custom_date) : dayjs();
    return date.add(hours, 'hours').toDate();
  }

  addMinutes(minutes: number, custom_date?: Date): Date {
    const date = custom_date ? dayjs(custom_date) : dayjs();
    return date.add(minutes, 'minutes').toDate();
  }

  subtract(unit: ManipulateType, quantity: number, custom_date?: Date): Date {
    const date = custom_date ? dayjs(custom_date) : dayjs();
    return date.subtract(quantity, unit).toDate();
  }

  subtractMonths(months: number, custom_date?: Date): Date {
    const date = custom_date ? dayjs(custom_date) : dayjs();
    return date.subtract(months, 'months').toDate();
  }

  startOfToday() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return dayjs(date).toDate();
  }

  eachDayOfInterval({
    startDate,
    endDate
  }: {
    startDate: Date;
    endDate: Date;
  }) {
    const days: Date[] = [];
    let currentDate = dayjs(startDate);

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
      days.push(currentDate.toDate());
      currentDate = currentDate.add(1, 'days');
    }

    return days;
  }

  format(date: Date, template: string): string {
    return dayjs(date).format(template);
  }

  firstDayOfMonth(month: number, custom_date?: Date) {
    const date = custom_date ? dayjs(custom_date) : dayjs().set('month', month);
    return date.startOf('month').toDate();
  }
}
